/**
 * V152: 근로일정 페이지
 *
 * 월 캘린더 + 근로일정 신청/변경 기능
 *
 * **기능:**
 * 1. 월 이동 가능한 캘린더 뷰
 * 2. 우상단 "근로일정 신청" 버튼 → 월 전체 base 설정
 * 3. 날짜 클릭 → 해당 날짜 변경 신청
 * 4. 상태 색상 표시 (회색/노랑/녹색)
 *
 * V2: 휴일 표시 추가
 * - 공휴일/회사휴일 셀에 배지로 휴일명 표시
 * - 주말(토/일) + 휴일 모두 시각적으로 구분
 *
 * **데이터 흐름:**
 * - 승인 전: approvalRequests에만 저장 (노랑)
 * - 승인 후: workSchedules SSoT에 반영 (녹색)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
// V162.2: Cloud Function 기반 단일 데이터 소스 (대시보드와 동일 훅 사용)
// V55.47: approvedLeaves, approvedOvertimes 추가
import {
  useApprovedWorkSchedule,
  ApprovedLeaveEvent,
  ApprovedOvertimeEvent,
} from '../../shared/hooks/useApprovedWorkSchedule';
// V55.47: 캘린더 이벤트 합성 유틸리티
import {
  synthesizeDailyEvents,
  SynthesizedCalendarEvent,
  getEventTypeColor,
} from '../../shared/utils/synthesizeDailyEvents';
// V5.13: 캘린더용 휴일 조회 (일반 직원 허용)
import { useCalendarHolidays, Holiday, CompanyCode } from '../../shared/hooks/useHolidays';
// V69 (2026-06-02): KR 격주 재택 금요일 공용 helper
import { isKrRemoteFriday } from '../../shared/utils/krRemoteFriday';
// V5.17: 휴일근무 승인 조회
import { useMyApprovalRequests } from '../../shared/hooks/useApprovalRequests';
import { ChevronLeft, ChevronRight, Plus, Clock, Edit2, Loader2, Sun, Palmtree } from 'lucide-react';
import { DailySchedule, CalendarDayData, DayScheduleStatus } from '../../shared/types/workSchedule';
// V280: 근로일정 템플릿
import {
  getTemplatesForCompany,
  WorkScheduleTemplate,
  TemplateGenerationResult,
  getDayLabel,
} from '../../shared/utils/workScheduleTemplates';

// ===== 24시간 형식 시간 선택 컴포넌트 =====
interface TimeSelectProps {
  value: string;
  onChange: (value: string) => void;
  style?: React.CSSProperties;
}

function TimeSelect({ value, onChange, style }: TimeSelectProps) {
  // 시간 옵션 생성 (00:00 ~ 23:30, 30분 단위)
  const timeOptions: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      timeOptions.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%',
        padding: '10px 12px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        fontSize: '16px',
        background: 'white',
        cursor: 'pointer',
        ...style,
      }}
    >
      {timeOptions.map((time) => (
        <option key={time} value={time}>
          {time}
        </option>
      ))}
    </select>
  );
}

// ===== 유틸리티 함수 =====

/**
 * 해당 월의 날짜 배열 생성 (캘린더 그리드용)
 */
function getMonthDays(year: number, month: number): Date[] {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const days: Date[] = [];

  // 첫째 주 시작일 (일요일 기준)
  const startPadding = firstDay.getDay();
  for (let i = startPadding - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push(d);
  }

  // 해당 월 날짜
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push(new Date(year, month, d));
  }

  // 마지막 주 채우기
  const endPadding = 6 - lastDay.getDay();
  for (let i = 1; i <= endPadding; i++) {
    days.push(new Date(year, month + 1, i));
  }

  return days;
}

/**
 * 날짜를 YYYY-MM-DD 형식으로 변환
 */
function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * 날짜를 YYYY-MM 형식으로 변환
 */
function formatMonth(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

// ===== 스타일 상수 =====

const STATUS_COLORS: Record<DayScheduleStatus, { bg: string; text: string; border: string }> = {
  none: { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' },      // 회색
  pending: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },  // 노랑
  approved: { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' }, // 녹색
};

// V162: spin 애니메이션 스타일 (인라인 keyframes)
const spinKeyframes = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// ===== V158.11: DayCell 컴포넌트 분리 (상태 변경 시 확실한 리렌더 보장) =====

interface DayCellProps {
  dateStr: string;
  day: Date;
  index: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  colors: { bg: string; text: string; border: string };
  schedule?: DailySchedule;
  dayData?: CalendarDayData;
  holiday?: Holiday | null;  // V2: 휴일 정보
  hasApprovedHolidayWork?: boolean;  // V5.17: 휴일근무 승인 여부
  synthesizedEvents?: SynthesizedCalendarEvent[];  // V55.47: 합성된 이벤트
  loaForCell?: { id: string; loaTypeName: string }[];  // V66: 셀 단위 휴직 이벤트
  isKrRemote?: boolean;  // V69 (2026-06-02): KR 격주 재택 금요일 표시 플래그 (부모에서 KR 가드 후 전달)
  onDayClick: (dateStr: string) => void;
}

function DayCell({
  dateStr,
  day,
  index,
  isCurrentMonth,
  isToday,
  colors,
  schedule,
  dayData,
  holiday,  // V2
  hasApprovedHolidayWork,  // V5.17
  synthesizedEvents,  // V55.47
  loaForCell,  // V66
  isKrRemote,  // V69 (2026-06-02): KR 격주 재택 금요일
  onDayClick,
}: DayCellProps) {
  const [isHovered, setIsHovered] = useState(false);

  // V158.11: hover 상태를 React state로 관리 → colors.bg 변경 시 정확히 반영
  // V2: 휴일인 경우 배경색 오버라이드
  // V69 (2026-06-02): KR 격주 재택 금요일 — 셀 배경은 변경하지 않음 (status 색상이 의미를 가지므로).
  //   조직 휴가 캘린더(OrgLeaveCalendarPage)는 셀 배경 노랑 적용 유지. 근로일정 캘린더는 헤더 옆 배지만.
  //   showKrRemoteVisual 은 헤더 배지 노출 여부 가드용 (휴일/비현재월 시 배지 미표시).
  const isHolidayCell = !!holiday;
  const showKrRemoteVisual = !!isKrRemote && !isHolidayCell && isCurrentMonth;
  const defaultBgColor = isHolidayCell ? '#FFF0F3' : colors.bg;  // 휴일은 옅은 분홍색 배경
  const bgColor = isHovered ? '#f0f0f0' : (isCurrentMonth ? defaultBgColor : '#fafafa');

  return (
    <div
      onClick={() => isCurrentMonth && onDayClick(dateStr)}
      onMouseEnter={() => isCurrentMonth && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        minHeight: '120px',
        padding: '10px',
        borderRight: (index + 1) % 7 !== 0 ? '1px solid #f3f4f6' : 'none',
        borderBottom: '1px solid #f3f4f6',
        background: bgColor,
        cursor: isCurrentMonth ? 'pointer' : 'default',
        opacity: isCurrentMonth ? 1 : 0.4,
        transition: 'background 0.2s',
      }}
    >
      {/* 날짜 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '4px',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
          <span style={{
            fontSize: '14px',
            fontWeight: isToday ? '700' : '500',
            color: isToday ? '#3b82f6' : (index % 7 === 0 ? '#ef4444' : index % 7 === 6 ? '#3b82f6' : '#374151'),
            background: isToday ? '#dbeafe' : 'transparent',
            padding: isToday ? '2px 6px' : '0',
            borderRadius: '4px',
            flexShrink: 0,
          }}>
            {day.getDate()}
          </span>
          {/* V69 (2026-06-02): KR 격주 재택 금요일 — 날짜 옆 pill badge.
              - 부모에서 companyCode === 'KR' 가드 후 isKrRemote=true 일 때만 전달
              - 비휴일 + 현재월 일 때만 표시 (휴일/비현재월이면 미노출)
              - 휴가/근태/지각/자동퇴근 영향 0. pointerEvents:'none' 으로 hover/popover 회귀 차단 */}
          {showKrRemoteVisual && (
            <span
              style={{
                fontSize: '9px',
                color: '#D97706',                  // muted amber
                fontWeight: 600,
                background: '#FEF3C7',             // 셀 배경 #FFFBEB 보다 살짝 진함
                border: '1px solid #FDE68A',       // pale amber border
                borderRadius: '999px',             // pill
                padding: '0px 6px',
                lineHeight: 1.6,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                flexShrink: 0,
                letterSpacing: '0.2px',
              }}
              title="격주 재택 금요일"
            >
              재택근무
            </span>
          )}
        </span>

        {dayData?.isOverride && isCurrentMonth && (
          <Edit2 size={12} style={{ color: '#6b7280' }} />
        )}
      </div>

      {/* V2: 휴일 배지 표시 */}
      {isCurrentMonth && holiday && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginBottom: '4px',
          padding: '2px 6px',
          background: '#FBCFE8',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: '500',
          color: '#9D174D',
        }}>
          <Sun size={10} />
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '80px',
          }}>
            {holiday.name}
          </span>
        </div>
      )}

      {/* V66: 휴직 이벤트 — 기간 전체(주말/공휴일 포함) 표시. 휴직 우선이라 휴가/OT 합성 이벤트보다 위에 노출 */}
      {isCurrentMonth && loaForCell && loaForCell.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '4px' }}>
          {loaForCell.map((loa) => (
            <div
              key={loa.id}
              title={`휴직중 (${loa.loaTypeName})`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                padding: '2px 6px',
                background: '#fff7ed',
                color: '#c2410c',
                border: '1px solid #fed7aa',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              휴직중 ({loa.loaTypeName})
            </div>
          ))}
        </div>
      )}

      {/* V55.47: 합성된 이벤트 표시 */}
      {isCurrentMonth && (!loaForCell || loaForCell.length === 0) && synthesizedEvents && synthesizedEvents.length > 0 && (() => {
        const dayOfWeek = day.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        const isHolidayDate = !!holiday;

        // 주말/공휴일에는 휴일근무 승인된 경우에만 표시
        // (단, 휴가/OT 이벤트는 항상 표시)
        const hasLeaveOrOT = synthesizedEvents.some(
          (e) => e.type === 'leave' || e.type === 'overtime'
        );

        if ((isWeekend || isHolidayDate) && !hasApprovedHolidayWork && !hasLeaveOrOT) {
          return null;
        }

        return (
          <div style={{ fontSize: '10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {synthesizedEvents.map((event, idx) => {
              const eventColor = getEventTypeColor(event.type);
              // V60: 주말/공휴일에 기본 근무일정(work)은 항상 숨김
              // 휴일근무 승인 시에도 승인된 OT 시간만 표시, 기본 근무일정은 노출하지 않음
              if (event.type === 'work' && (isWeekend || isHolidayDate)) {
                return null;
              }
              return (
                <div
                  key={`${event.type}-${idx}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                    padding: '2px 4px',
                    background: eventColor.bg,
                    borderRadius: '3px',
                    color: eventColor.text,
                  }}
                >
                  {event.type === 'leave' ? <Palmtree size={8} /> : <Clock size={8} />}
                  {/* V64: maxWidth 제한 해제 (이전 70px로 잘리던 문제 해결) */}
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>
                    {event.title}
                  </span>
                  {/* V55.47.1: 시간 정보가 있을 때만 표시. V64: 타이틀에 이미 시간 범위가 포함된 경우(hourly) 중복 표기 생략 */}
                  {event.startTime && event.endTime && !event.title.includes('~') && (
                    <span style={{ fontSize: '9px', opacity: 0.8 }}>
                      {event.startTime}-{event.endTime}
                      {event.endDateOffset === 1 && '+1'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

// ===== 컴포넌트 =====

export function WorkSchedulePage() {
  const { user } = useCurrentUser();
  const companyCode = user?.profile?.companyCode as CompanyCode | undefined;
  const [currentDate, setCurrentDate] = useState(new Date());

  // 현재 월
  const currentMonth = formatMonth(currentDate);
  const currentYear = currentDate.getFullYear();
  const monthDays = useMemo(() => getMonthDays(currentDate.getFullYear(), currentDate.getMonth()), [currentDate]);

  // V5.13.1: 캘린더용 휴일 조회 (companyCode가 null이면 훅 내부에서 빈 배열 처리)
  const { holidays: yearHolidays } = useCalendarHolidays(companyCode ?? null, currentYear);

  // V2: 휴일을 날짜 기준으로 Map 생성 (빠른 조회용)
  const holidayMap = useMemo(() => {
    const map = new Map<string, Holiday>();
    if (yearHolidays) {
      yearHolidays.forEach((h) => {
        if (h.status === 'active') {
          map.set(h.date, h);
        }
      });
    }
    return map;
  }, [yearHolidays]);

  // V5.17: 휴일근무 승인 요청 조회
  const { requests: holidayWorkRequests } = useMyApprovalRequests({
    year: currentYear,
    type: 'holidayWork',
  });

  // V162.2: Cloud Function 기반 단일 데이터 소스 (대시보드와 동일 훅 사용)
  // - 동일한 month로 대시보드에서 이미 조회했다면 캐시된 데이터로 즉시 렌더링
  // - 로딩/에러 상태도 훅에서 관리
  // V55.47: approvedLeaves, approvedOvertimes 추가
  const {
    workSchedule,
    pendingRequests: serverPendingRequests,
    approvedLeaves,     // V55.47
    approvedOvertimes,  // V55.47
    approvedLeavesOfAbsence,  // V66
    isLoading: hookLoading,
    isError,
    errorMessage,
    refetch,
  } = useApprovedWorkSchedule(currentMonth);

  // V5.17: 승인된 휴일근무 날짜 Set 생성 (빠른 조회용)
  // V64: approvedOvertimes(CF 반환)에서 weekendWork/holidayWork도 포함
  const approvedHolidayWorkDates = useMemo(() => {
    const dates = new Set<string>();
    if (holidayWorkRequests) {
      holidayWorkRequests.forEach((r) => {
        if (r.status === 'approved' && r.requestData?.date) {
          dates.add(r.requestData.date);
        }
      });
    }
    // CF에서 반환된 승인된 OT 중 weekendWork/holidayWork도 추가
    if (approvedOvertimes) {
      approvedOvertimes.forEach((ot) => {
        if (ot.type === 'weekendWork' || ot.type === 'holidayWork') {
          dates.add(ot.date);
        }
      });
    }
    return dates;
  }, [holidayWorkRequests, approvedOvertimes]);

  // V162.2: optimistic pending을 위한 로컬 state
  // - 서버 데이터와 병합하여 즉각적인 UI 반영
  const [optimisticPending, setOptimisticPending] = useState<any[]>([]);

  // V163: 서버 데이터 + optimistic 데이터 병합 (merge + dedupe)
  const pendingRequests = useMemo(() => {
    // 서버 데이터를 기본으로
    const merged = [...serverPendingRequests];
    const serverIds = new Set(serverPendingRequests.map((r) => r.id));

    // optimistic 중 서버에 없는 것만 추가 (중복 방지)
    for (const opt of optimisticPending) {
      // optimistic ID는 항상 'optimistic-'으로 시작하므로 서버 ID와 충돌 없음
      // 단, 같은 type + month/date 조합이면 서버 데이터 우선
      const isDuplicate = serverPendingRequests.some((s) => {
        if (opt.type === 'workSchedule' && s.type === 'workSchedule') {
          return opt.requestData?.month === s.requestData?.month;
        }
        if (opt.type === 'workScheduleChange' && s.type === 'workScheduleChange') {
          return opt.requestData?.date === s.requestData?.date;
        }
        return false;
      });

      if (!isDuplicate && !serverIds.has(opt.id)) {
        merged.push(opt);
      }
    }

    return merged;
  }, [serverPendingRequests, optimisticPending]);

  // V162.2: 로딩 상태 (훅 로딩 상태 사용)
  const isLoading = hookLoading;
  const error = isError ? errorMessage : null;

  // 팝업 상태
  const [showRequestPopup, setShowRequestPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  // V66: 근로일정 일괄수정 모달
  const [showBulkEditPopup, setShowBulkEditPopup] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // V162.2: 월 변경 시 optimistic 초기화
  useEffect(() => {
    setOptimisticPending([]);
  }, [currentMonth]);

  // ===== 캘린더 날짜별 상태 계산 =====
  // V158.12: 디버그 로그 - 렌더링 시 pendingRequests 상태 확인
  const calendarData = useMemo((): Map<string, CalendarDayData> => {
    const map = new Map<string, CalendarDayData>();
    let pendingCount = 0;

    for (const day of monthDays) {
      const dateStr = formatDate(day);

      // 해당 월 외 날짜는 건너뛰기
      if (!dateStr.startsWith(currentMonth)) {
        continue;
      }

      // 기본값
      let status: DayScheduleStatus = 'none';
      let schedule: DailySchedule | undefined;
      let pendingSchedule: DailySchedule | undefined;
      let isOverride = false;
      let approvalRequestId: string | undefined;

      // 1. 승인완료된 workSchedule이 있는지 확인
      if (workSchedule?.base) {
        // base가 있으면 기본적으로 approved
        schedule = workSchedule.base;
        status = 'approved';

        // V280.1: dailyOverrides 반영 (off-day sentinel 포함)
        if (workSchedule.dailyOverrides?.[dateStr]) {
          const override = workSchedule.dailyOverrides[dateStr];
          if (override.startTime === '') {
            // off-day sentinel (놀금 등) → 비근무
            schedule = undefined;
          } else {
            schedule = { ...schedule, ...override };
          }
          isOverride = true;
        }
      }

      // 2. pending 요청이 있는지 확인
      const monthPending = pendingRequests.find(
        (r) => r.type === 'workSchedule' && r.requestData?.month === currentMonth
      );
      if (monthPending) {
        status = 'pending';
        // V280.1: pending 요청의 dailyOverrides도 날짜별 반영
        const pendingOverrides = monthPending.requestData?.dailyOverrides;
        if (pendingOverrides?.[dateStr]) {
          const override = pendingOverrides[dateStr];
          if (override.startTime === '') {
            pendingSchedule = undefined; // off-day
          } else {
            pendingSchedule = { ...monthPending.requestData?.base, ...override };
          }
        } else {
          pendingSchedule = monthPending.requestData?.base;
        }
        approvalRequestId = monthPending.id;
        pendingCount++;
      }

      // 날짜별 pending 요청
      const datePending = pendingRequests.find(
        (r) => r.type === 'workScheduleChange' && r.requestData?.date === dateStr
      );
      if (datePending) {
        status = 'pending';
        pendingSchedule = {
          ...(schedule || {}),
          ...datePending.requestData?.changes,
        } as DailySchedule;
        approvalRequestId = datePending.id;
      }

      map.set(dateStr, {
        date: dateStr,
        status,
        schedule,
        pendingSchedule,
        isOverride,
        approvalRequestId,
      });
    }

    return map;
  }, [monthDays, currentMonth, workSchedule, pendingRequests]);

  // ===== 이벤트 핸들러 =====

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDayClick = (dateStr: string) => {
    // 현재 월 외 날짜는 무시
    if (!dateStr.startsWith(currentMonth)) return;

    const dayData = calendarData.get(dateStr);

    // base가 없으면 먼저 월 신청이 필요
    if (!workSchedule?.base) {
      alert('먼저 월 근로일정을 신청해주세요.');
      setShowRequestPopup(true);
      return;
    }

    // 해당 날짜에 pending 요청이 있으면 수정 불가
    if (dayData?.status === 'pending' && dayData.approvalRequestId) {
      alert('이미 승인 대기 중인 요청이 있습니다.');
      return;
    }

    setSelectedDate(dateStr);
    setShowEditPopup(true);
  };

  // ===== 렌더링 =====

  const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

  return (
    <div style={{ paddingTop: '48px' }}>
      {/* V162: spin 애니메이션 keyframes 주입 */}
      <style dangerouslySetInnerHTML={{ __html: spinKeyframes }} />

      {/* 헤더 */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto 24px auto',
      }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-gray-800)' }}>
          근로 일정
        </h1>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '16px',
        }}>
          {error}
        </div>
      )}

      {/* 월 네비게이션 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '24px',
        marginBottom: '24px'
      }}>
        <button
          onClick={handlePrevMonth}
          style={{
            padding: '8px',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          <ChevronLeft size={20} />
        </button>

        <span style={{ fontSize: '20px', fontWeight: '600' }}>
          {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
        </span>

        <button
          onClick={handleNextMonth}
          style={{
            padding: '8px',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* 상태 범례 + 신청 버튼 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '1200px',
        margin: '0 auto 16px auto',
      }}>
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '16px', height: '16px', background: STATUS_COLORS.none.bg, borderRadius: '4px', border: `1px solid ${STATUS_COLORS.none.border}` }} />
            <span style={{ fontSize: '12px', color: '#6b7280' }}>요청없음</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '16px', height: '16px', background: STATUS_COLORS.pending.bg, borderRadius: '4px', border: `1px solid ${STATUS_COLORS.pending.border}` }} />
            <span style={{ fontSize: '12px', color: '#6b7280' }}>승인대기</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '16px', height: '16px', background: STATUS_COLORS.approved.bg, borderRadius: '4px', border: `1px solid ${STATUS_COLORS.approved.border}` }} />
            <span style={{ fontSize: '12px', color: '#6b7280' }}>승인완료</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* V66: 근로일정 일괄수정 — base 가 있을 때만 활성화 (BE 정책과 일치) */}
          <button
            onClick={() => setShowBulkEditPopup(true)}
            disabled={isLoading || !workSchedule?.base}
            title={!workSchedule?.base ? '월 근로일정 신청·승인 후 일괄수정이 가능합니다.' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: !workSchedule?.base ? '#e5e7eb' : '#10b981',
              color: !workSchedule?.base ? '#9ca3af' : 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: !workSchedule?.base ? 'not-allowed' : 'pointer',
            }}
          >
            근로일정 일괄수정
          </button>
          <button
            onClick={() => setShowRequestPopup(true)}
            disabled={isLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            <Plus size={18} />
            근로일정 신청
          </button>
        </div>
      </div>

      {/* V162: 캘린더 - 로딩 오버레이 추가 */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
      }}>
        {/* V162: 로딩 오버레이 */}
        {isLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
            }}>
              <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
              <span style={{ fontSize: '14px', color: '#6b7280' }}>근로일정 불러오는 중...</span>
            </div>
          </div>
        )}

        {/* 요일 헤더 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          background: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
        }}>
          {weekDays.map((day, i) => (
            <div
              key={day}
              style={{
                padding: '12px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '600',
                color: i === 0 ? '#ef4444' : i === 6 ? '#3b82f6' : '#374151',
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* 날짜 그리드 - V158.11: key에 status 포함하여 pending 변경 시 강제 리렌더 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
        }}>
          {monthDays.map((day, index) => {
            const dateStr = formatDate(day);
            const isCurrentMonth = dateStr.startsWith(currentMonth);
            const isToday = dateStr === formatDate(new Date());
            const dayData = calendarData.get(dateStr);
            const status = dayData?.status || 'none';
            const colors = STATUS_COLORS[status];
            const schedule = dayData?.pendingSchedule || dayData?.schedule;

            // V2: 해당 날짜의 휴일 정보
            const holiday = holidayMap.get(dateStr) || null;

            // V5.17: 해당 날짜에 승인된 휴일근무가 있는지 확인
            const hasApprovedHolidayWork = approvedHolidayWorkDates.has(dateStr);

            // V55.47: 캘린더 이벤트 합성
            // V64: schedule===undefined(놀금/off-day)에서도 합성 필요 — synthesizeDailyEvents가 !schedule 분기 처리
            // V66: 주말/공휴일 셀에서는 휴가 표시 제외 (다일 휴가 expand 가드)
            //   - 정책: 휴가가 주말/공휴일을 포함해도 그 날짜에는 표시하지 않음 (실제 근무일 기준)
            //   - OT(연장/휴일근무)는 의도적으로 주말/공휴일 발생 가능하므로 그대로 유지
            const dowForLeaveFilter = day.getDay();
            const isWeekendForLeave = dowForLeaveFilter === 0 || dowForLeaveFilter === 6;
            const isHolidayForLeave = holidayMap.has(dateStr);
            const leavesForCell =
              isWeekendForLeave || isHolidayForLeave ? [] : approvedLeaves;
            const synthesizedEvents = isCurrentMonth
              ? synthesizeDailyEvents(dateStr, schedule, leavesForCell, approvedOvertimes)
              : undefined;

            // V66: 휴직 셀 매칭 — 기간 전체(주말/공휴일 포함) 표시
            const loaForCell = isCurrentMonth && approvedLeavesOfAbsence
              ? approvedLeavesOfAbsence
                  .filter((l) => l.startDate <= dateStr && l.endDate >= dateStr)
                  .map((l) => ({ id: l.id, loaTypeName: l.loaTypeName || '휴직' }))
              : [];

            // V69 (2026-06-02): KR 격주 재택 금요일 — DB 저장 / Firestore patch / 스케줄러 0.
            //   companyCode === 'KR' 일 때만 isKrRemoteFriday 적용. CN 미표시.
            const isKrRemoteCell = companyCode === 'KR' && isKrRemoteFriday(day);

            return (
              <DayCell
                key={`${dateStr}-${status}`}
                dateStr={dateStr}
                day={day}
                index={index}
                isCurrentMonth={isCurrentMonth}
                isToday={isToday}
                colors={colors}
                schedule={schedule}
                dayData={dayData}
                holiday={holiday}
                hasApprovedHolidayWork={hasApprovedHolidayWork}
                synthesizedEvents={synthesizedEvents}
                loaForCell={loaForCell}
                isKrRemote={isKrRemoteCell}
                onDayClick={handleDayClick}
              />
            );
          })}
        </div>
      </div>

      {/* 현재 월 근로일정 정보 */}
      {workSchedule?.base && (
        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          maxWidth: '1200px',
          margin: '24px auto 0',
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
            {currentMonth} 기본 근무시간
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            <div>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>출근</span>
              <div style={{ fontSize: '16px', fontWeight: '600' }}>{workSchedule.base.startTime}</div>
            </div>
            <div>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>점심 시작</span>
              <div style={{ fontSize: '16px', fontWeight: '600' }}>{workSchedule.base.lunchStart}</div>
            </div>
            <div>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>점심 종료</span>
              <div style={{ fontSize: '16px', fontWeight: '600' }}>{workSchedule.base.lunchEnd}</div>
            </div>
            <div>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>퇴근</span>
              <div style={{ fontSize: '16px', fontWeight: '600' }}>{workSchedule.base.endTime}</div>
            </div>
          </div>
        </div>
      )}

      {/* 월 근로일정 신청 팝업 */}
      {showRequestPopup && (
        <WorkScheduleRequestPopup
          month={currentMonth}
          existingBase={workSchedule?.base}
          companyCode={companyCode}
          onClose={() => setShowRequestPopup(false)}
          onSuccess={(result) => {
            setShowRequestPopup(false);

            // V162.2: 낙관적 UI + refetch 기반 동기화 (loadData → refetch)
            if (result?.optimistic && result.pendingBase && result.cfPromise) {
              const optimisticId = `optimistic-${Date.now()}`;

              // 1. 즉시 낙관적 pending 추가 (캘린더 노란색)
              setOptimisticPending((prev) => [
                ...prev,
                {
                  id: optimisticId,
                  type: 'workSchedule',
                  status: 'pending',
                  requestData: {
                    month: currentMonth,
                    base: result.pendingBase,
                    ...(result.pendingDailyOverrides ? { dailyOverrides: result.pendingDailyOverrides } : {}),
                  },
                },
              ]);

              // 2. CF 완료 시 실제 데이터 동기화 (refetch 호출)
              result.cfPromise
                .then(() => {
                  refetch();
                })
                .catch((err: any) => {
                  // 3. 실패 시 롤백: optimistic 항목 제거
                  setOptimisticPending((prev) => prev.filter((p) => p.id !== optimisticId));
                  alert(`신청 실패: ${err.message || '알 수 없는 오류'}`);
                });
            } else {
              refetch();
            }
          }}
        />
      )}

      {/* 날짜별 변경 신청 팝업 */}
      {showEditPopup && selectedDate && (
        <WorkScheduleEditPopup
          month={currentMonth}
          date={selectedDate}
          currentSchedule={calendarData.get(selectedDate)?.schedule}
          onClose={() => {
            setShowEditPopup(false);
            setSelectedDate(null);
          }}
          onSuccess={() => {
            setShowEditPopup(false);
            setSelectedDate(null);
            // V162.2: loadData → refetch
            refetch();
          }}
        />
      )}

      {/* V66: 근로일정 일괄수정 팝업 */}
      {showBulkEditPopup && (
        <WorkScheduleBulkEditPopup
          month={currentMonth}
          currentBase={workSchedule?.base}
          companyCode={companyCode === 'CN' ? 'CN' : 'KR'}
          onClose={() => setShowBulkEditPopup(false)}
          onSuccess={() => {
            setShowBulkEditPopup(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

// ===== 월 근로일정 신청 팝업 =====

// V158.9: 낙관적 UI 결과 타입 (CF promise 포함)
interface OptimisticResult {
  optimistic: boolean;
  pendingBase?: DailySchedule;
  pendingDailyOverrides?: Record<string, Partial<DailySchedule>>; // V280.1
  cfPromise?: Promise<any>;
}

interface WorkScheduleRequestPopupProps {
  month: string;
  existingBase?: DailySchedule;
  companyCode?: string | null; // V280: 템플릿 필터링용
  onClose: () => void;
  onSuccess: (result?: OptimisticResult) => void;
}

function WorkScheduleRequestPopup({ month, existingBase, companyCode, onClose, onSuccess }: WorkScheduleRequestPopupProps) {
  const [startTime, setStartTime] = useState(existingBase?.startTime || '10:00');
  const [lunchStart, setLunchStart] = useState(existingBase?.lunchStart || '12:00');
  const [lunchEnd, setLunchEnd] = useState(existingBase?.lunchEnd || '13:00');
  const [endTime, setEndTime] = useState(existingBase?.endTime || '19:00');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // V280: 템플릿 기능
  const templates = useMemo(() => getTemplatesForCompany(companyCode), [companyCode]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [templateResult, setTemplateResult] = useState<TemplateGenerationResult | null>(null);

  // V280.1: 템플릿 모드 여부 (템플릿 선택 시 시간 입력 UI 숨김)
  const isTemplateMode = !!selectedTemplateId && !!templateResult;

  // V280: 템플릿 선택 시 base 자동 적용 + 상세 생성
  const handleTemplateSelect = (templateId: string | null) => {
    setSelectedTemplateId(templateId);
    if (!templateId) {
      setTemplateResult(null);
      return;
    }
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;

    const [yearStr, monthStr] = month.split('-');
    const result = template.generate(Number(yearStr), Number(monthStr));
    setTemplateResult(result);

    // base 시간 자동 설정 (내부 state 동기화)
    setStartTime(result.base.startTime);
    setLunchStart(result.base.lunchStart);
    setLunchEnd(result.base.lunchEnd);
    setEndTime(result.base.endTime);
  };

  // V280.1: 템플릿 overrides + 놀금 → dailyOverrides 변환
  // 월/금 = base와 다른 스케줄 → dailyOverrides로 전달
  // 놀금 = 휴무 센티널 (startTime='') → 화면/계산에서 비근무 처리
  const buildDailyOverrides = (): Record<string, Partial<DailySchedule>> | undefined => {
    if (!templateResult) return undefined;
    const overrides: Record<string, Partial<DailySchedule>> = {};

    // 월/금 등 base와 다른 요일별 스케줄
    for (const entry of templateResult.overrides) {
      if (!entry.schedule) continue;
      overrides[entry.date] = {
        startTime: entry.schedule.startTime,
        lunchStart: entry.schedule.lunchStart,
        lunchEnd: entry.schedule.lunchEnd,
        endTime: entry.schedule.endTime,
      };
    }

    // 놀금 = off-day sentinel (startTime='' → 비근무)
    for (const pfDate of templateResult.playFridays) {
      overrides[pfDate] = { startTime: '', endTime: '', lunchStart: '', lunchEnd: '' };
    }

    return Object.keys(overrides).length > 0 ? overrides : undefined;
  };

  // V158.9: 낙관적 UI + CF promise 전달
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const functions = getFunctions();
      const createWorkScheduleRequestFn = httpsCallable(functions, 'createWorkScheduleRequest');

      const base = { startTime, lunchStart, lunchEnd, endTime };

      // V280.1: 템플릿 모드일 때 dailyOverrides 함께 전송
      const dailyOverrides = isTemplateMode ? buildDailyOverrides() : undefined;

      // V66: 템플릿 사용 시 templateId / templateName 함께 전송 (승인요청관리 표시용)
      const templateMeta = isTemplateMode && templateResult
        ? { templateId: templateResult.templateId, templateName: templateResult.templateName }
        : undefined;

      const cfPromise = createWorkScheduleRequestFn({
        month,
        base,
        ...(dailyOverrides ? { dailyOverrides } : {}),
        ...(templateMeta ? templateMeta : {}),
      });

      onSuccess({
        optimistic: true,
        pendingBase: base,
        pendingDailyOverrides: dailyOverrides,
        cfPromise,
      });
    } catch (err: any) {
      console.error('[V158.9] 근로일정 신청 초기화 실패:', err);
      setError(err.message || '신청에 실패했습니다.');
      setIsSubmitting(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        width: '400px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px' }}>
          {month} 근로일정 신청
        </h2>

        {error && (
          <div style={{
            padding: '12px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '16px',
            fontSize: '14px',
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* V280.1: 직접 입력 모드 — 시간 입력 UI (템플릿 선택 시 숨김) */}
          {!isTemplateMode && (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                  출근 시간
                </label>
                <TimeSelect value={startTime} onChange={setStartTime} />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                  점심 시작
                </label>
                <TimeSelect value={lunchStart} onChange={setLunchStart} />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                  점심 종료
                </label>
                <TimeSelect value={lunchEnd} onChange={setLunchEnd} />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                  퇴근 시간
                </label>
                <TimeSelect value={endTime} onChange={setEndTime} />
              </div>
            </>
          )}

          {/* V280.1: 템플릿 선택 — 시간 입력 아래에 위치 */}
          {templates.length > 0 && (
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                템플릿 선택
              </label>
              <select
                value={selectedTemplateId || ''}
                onChange={(e) => handleTemplateSelect(e.target.value || null)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                <option value="">직접 입력</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>

              {/* V280.1: 템플릿 상세 미리보기 */}
              {isTemplateMode && (
                <div style={{
                  marginTop: '8px',
                  padding: '12px',
                  background: '#eff6ff',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#1e40af',
                  lineHeight: 1.7,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '14px' }}>
                    {templateResult.templateName} 스케줄
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    기본(base): 화~목 {templateResult.base.startTime}~{templateResult.base.endTime}
                    <span style={{ fontSize: '11px', color: '#6b7280' }}> (점심 {templateResult.base.lunchStart}~{templateResult.base.lunchEnd})</span>
                  </div>
                  {templateResult.overrides.length > 0 && (
                    <div style={{ marginBottom: '4px' }}>
                      요일별 차등:
                      {(() => {
                        const byDay = new Map<number, typeof templateResult.overrides[0]>();
                        templateResult.overrides.forEach((o) => {
                          if (!byDay.has(o.dayOfWeek)) byDay.set(o.dayOfWeek, o);
                        });
                        return Array.from(byDay.entries()).map(([dow, entry]) => (
                          <div key={dow} style={{ marginLeft: '8px' }}>
                            {getDayLabel(dow)}: {entry.schedule?.startTime}~{entry.schedule?.endTime}
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                  {templateResult.playFridays.length > 0 && (
                    <div style={{ marginBottom: '4px' }}>
                      놀금({templateResult.playFridays.length}일): {templateResult.playFridays.map((d) => d.slice(5)).join(', ')}
                    </div>
                  )}
                  <div style={{
                    marginTop: '8px',
                    padding: '8px',
                    background: '#dbeafe',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#1e40af',
                  }}>
                    승인 시 base + 요일별 차등 스케줄이 한 번에 적용됩니다.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: '12px',
              background: '#f3f4f6',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: '12px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            {isSubmitting ? '신청 중...' : '신청하기'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ===== 날짜별 변경 신청 팝업 =====

interface WorkScheduleEditPopupProps {
  month: string;
  date: string;
  currentSchedule?: DailySchedule;
  onClose: () => void;
  onSuccess: () => void;
}

function WorkScheduleEditPopup({ month, date, currentSchedule, onClose, onSuccess }: WorkScheduleEditPopupProps) {
  const [startTime, setStartTime] = useState(currentSchedule?.startTime || '10:00');
  const [lunchStart, setLunchStart] = useState(currentSchedule?.lunchStart || '12:00');
  const [lunchEnd, setLunchEnd] = useState(currentSchedule?.lunchEnd || '13:00');
  const [endTime, setEndTime] = useState(currentSchedule?.endTime || '19:00');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    // 변경사항 확인
    const changes: Partial<DailySchedule> = {};
    if (startTime !== currentSchedule?.startTime) changes.startTime = startTime;
    if (lunchStart !== currentSchedule?.lunchStart) changes.lunchStart = lunchStart;
    if (lunchEnd !== currentSchedule?.lunchEnd) changes.lunchEnd = lunchEnd;
    if (endTime !== currentSchedule?.endTime) changes.endTime = endTime;

    if (Object.keys(changes).length === 0) {
      alert('변경된 내용이 없습니다.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const functions = getFunctions();
      const createWorkScheduleChangeRequestFn = httpsCallable(functions, 'createWorkScheduleChangeRequest');

      await createWorkScheduleChangeRequestFn({
        month,
        date,
        changes,
        reason,
      });

      alert('변경 신청이 완료되었습니다. 승인을 기다려주세요.');
      onSuccess();
    } catch (err: any) {
      console.error('변경 신청 실패:', err);
      setError(err.message || '신청에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 날짜 포맷팅
  const dateObj = new Date(date);
  const formattedDate = `${dateObj.getMonth() + 1}월 ${dateObj.getDate()}일`;

  return (
    <ModalOverlay onClose={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        width: '400px',
        maxWidth: '90vw',
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px' }}>
          {formattedDate} 근무시간 변경
        </h2>

        {error && (
          <div style={{
            padding: '12px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '16px',
            fontSize: '14px',
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
              출근 시간
            </label>
            <TimeSelect value={startTime} onChange={setStartTime} />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
              점심 시작
            </label>
            <TimeSelect value={lunchStart} onChange={setLunchStart} />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
              점심 종료
            </label>
            <TimeSelect value={lunchEnd} onChange={setLunchEnd} />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
              퇴근 시간
            </label>
            <TimeSelect value={endTime} onChange={setEndTime} />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
              변경 사유 (선택)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="예: 외부 미팅으로 인한 시간 변경"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: '12px',
              background: '#f3f4f6',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: '12px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            {isSubmitting ? '신청 중...' : '변경 신청'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ===== V66: 근로일정 일괄수정 팝업 =====

interface WorkScheduleBulkEditPopupProps {
  month: string; // "YYYY-MM"
  currentBase?: DailySchedule;
  companyCode: 'KR' | 'CN';
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * V66: 근로일정 일괄수정 팝업
 * - 신청 시점의 KST 오늘을 포함한 미래 날짜 + 같은 month 안에서만 선택 가능
 * - 출/점심시작/점심종료/퇴근 4개 시간을 선택한 모든 날짜에 동일 적용
 * - BE callable: createWorkScheduleBulkChangeRequest
 */
function WorkScheduleBulkEditPopup({
  month,
  currentBase,
  companyCode,
  onClose,
  onSuccess,
}: WorkScheduleBulkEditPopupProps) {
  const baseDefaults: DailySchedule = currentBase || {
    startTime: '10:00',
    lunchStart: '12:00',
    lunchEnd: '13:00',
    endTime: '19:00',
  };

  const [startTime, setStartTime] = useState(baseDefaults.startTime);
  const [lunchStart, setLunchStart] = useState(baseDefaults.lunchStart);
  const [lunchEnd, setLunchEnd] = useState(baseDefaults.lunchEnd);
  const [endTime, setEndTime] = useState(baseDefaults.endTime);
  // V66 (range mode): 날짜 1개 클릭 → 해당 날짜~월 말일 자동 선택.
  //   기존 toggle/Set 기반에서 selectedStartDate 단일 상태로 전환.
  //   selectedDates 는 selectedStartDate 기반 useMemo 로 파생 → 기존 .size / .has / Array.from 호출부 재사용.
  const [selectedStartDate, setSelectedStartDate] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const templates = useMemo(() => getTemplatesForCompany(companyCode), [companyCode]);

  // 캘린더 그리드 (해당 월 + 양옆 padding)
  const [year, monthNum] = month.split('-').map(Number);
  const monthDaysArr = useMemo(
    () => getMonthDays(year, monthNum - 1),
    [year, monthNum],
  );

  // KST 오늘 — formatDate(new Date()) 가 클라이언트 로컬타임이라 KST 사용자 기준으로 충분
  const todayStr = formatDate(new Date());

  const isSelectableDate = (dateStr: string): boolean => {
    if (!dateStr.startsWith(month)) return false; // padding 날짜 제외
    return dateStr >= todayStr;
  };

  // V66 (range mode): selectedStartDate ~ 월 말일 사이의 selectable 날짜를 자동 산출
  const selectedDates = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    if (!selectedStartDate) return set;
    monthDaysArr.forEach((d) => {
      const ds = formatDate(d);
      if (isSelectableDate(ds) && ds >= selectedStartDate) {
        set.add(ds);
      }
    });
    return set;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStartDate, monthDaysArr, todayStr, month]);

  const handleDateClick = (dateStr: string) => {
    if (!isSelectableDate(dateStr)) return;
    // 같은 시작일을 다시 클릭하면 해제
    setSelectedStartDate((prev) => (prev === dateStr ? null : dateStr));
  };

  // 오늘부터 월 말일까지 선택 (남은 일 전체 = startDate=오늘)
  const selectAllRemaining = () => {
    setSelectedStartDate(todayStr.startsWith(month) ? todayStr : null);
  };

  const clearSelected = () => setSelectedStartDate(null);

  const handleTemplateSelect = (id: string | null) => {
    setSelectedTemplateId(id);
    if (!id) return;
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    // V66c-2: 템플릿은 generate(year, month1idx) — 월 신청 line 980 과 동일 정책 (1-indexed)
    const result = tpl.generate(year, monthNum);
    if (result?.base) {
      setStartTime(result.base.startTime);
      setLunchStart(result.base.lunchStart);
      setLunchEnd(result.base.lunchEnd);
      setEndTime(result.base.endTime);
    }
  };

  const handleSubmit = async () => {
    if (selectedDates.size === 0) {
      setError('적용할 날짜를 1개 이상 선택해주세요.');
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'createWorkScheduleBulkChangeRequest');
      const tpl = selectedTemplateId
        ? templates.find((t) => t.id === selectedTemplateId)
        : null;

      const datesArr = Array.from(selectedDates).sort();

      // V66c (weekday templates + off sentinel): 템플릿 선택 시 선택한 모든 날짜를 perDateChanges 로 authoritative 표현.
      //   - 근무일: 해당 요일의 schedule (월 10:30~19:00, 화-목 base, 금 단축 등)
      //   - off (주말/놀금): { startTime:'', lunchStart:'', lunchEnd:'', endTime:'' } sentinel
      //     → workSchedules 의 단일 월 신청과 동일 정책 (SSoT) → calculateMonthlyWorkStats 가 비근무로 인식
      //   - perDateChanges 가 모든 dates 를 cover 하므로 BE handler 의 fallback `changes` 발동 안 함
      const OFF_SENTINEL: DailySchedule = { startTime: '', lunchStart: '', lunchEnd: '', endTime: '' };
      let perDateChanges: Record<string, DailySchedule> | undefined;
      if (tpl) {
        // V66c-2: monthNum 은 1-indexed (월 신청 line 980 과 동일 정책)
        const tplResult = tpl.generate(year, monthNum);
        const dayMap = new Map<string, DailySchedule | null>();
        tplResult.days.forEach((d) => dayMap.set(d.date, d.schedule));

        // 진단 로그 — 사용자 환경에서 캡처용
        console.log('[V66c-2 bulk] datesArr', datesArr);
        console.log('[V66c-2 bulk] tplResult.days[0..2]', tplResult.days.slice(0, 3));
        console.log('[V66c-2 bulk] dayMap keys[0..3]', Array.from(dayMap.keys()).slice(0, 3));

        const out: Record<string, DailySchedule> = {};
        for (const d of datesArr) {
          const sched = dayMap.get(d);
          if (!sched && !dayMap.has(d)) {
            console.warn('[V66c-2 bulk] dayMap miss — 키 매칭 실패!', d);
          }
          out[d] = sched ?? OFF_SENTINEL;
        }
        perDateChanges = out;
        console.log('[V66c-2 bulk] perDateChanges 합계',
          Object.keys(out).length,
          '/ off',
          Object.values(out).filter(v => v.startTime === '').length);
      }

      await fn({
        month,
        dates: datesArr,
        changes: { startTime, lunchStart, lunchEnd, endTime },
        ...(perDateChanges ? { perDateChanges } : {}),
        templateId: selectedTemplateId || undefined,
        templateName: tpl?.name || undefined,
        reason,
      });

      alert(
        `일괄수정 신청이 완료되었습니다 (${datesArr.length}일).\n승인을 기다려주세요.`,
      );
      onSuccess();
    } catch (err: any) {
      console.error('[V66] 근로일정 일괄수정 신청 실패:', err);
      setError(err.message || '신청에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const weekDayLabels = ['일', '월', '화', '수', '목', '금', '토'];

  return (
    <ModalOverlay
      onClose={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          width: '520px',
          maxWidth: '92vw',
          maxHeight: '92vh',
          overflowY: 'auto',
        }}
      >
        <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>
          {month} 근로일정 일괄수정
        </h2>
        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
          시작 날짜를 1번 클릭하면 <strong>해당 날짜부터 이번 달 말일까지</strong> 자동 선택됩니다.
          오늘({todayStr}) 이전 날짜는 선택할 수 없으며, 신청 후 승인 완료 시에만 캘린더에 반영됩니다.
        </p>

        {error && (
          <div
            style={{
              padding: '12px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#dc2626',
              marginBottom: '16px',
              fontSize: '14px',
              whiteSpace: 'pre-line',
            }}
          >
            {error}
          </div>
        )}

        {/* 1) 적용 날짜 선택 */}
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
            }}
          >
            <label style={{ fontSize: '14px', fontWeight: 500 }}>
              적용 날짜 선택 ({selectedDates.size}일)
            </label>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                type="button"
                onClick={selectAllRemaining}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  background: '#eff6ff',
                  color: '#1d4ed8',
                  border: '1px solid #bfdbfe',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                남은 일 전체
              </button>
              <button
                type="button"
                onClick={clearSelected}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                선택 해제
              </button>
            </div>
          </div>

          <div
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                background: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              {weekDayLabels.map((d, i) => (
                <div
                  key={d}
                  style={{
                    padding: '6px 0',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: i === 0 ? '#ef4444' : i === 6 ? '#3b82f6' : '#374151',
                  }}
                >
                  {d}
                </div>
              ))}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
              }}
            >
              {monthDaysArr.map((day, idx) => {
                const ds = formatDate(day);
                const inMonth = ds.startsWith(month);
                const selectable = isSelectableDate(ds);
                const selected = selectedDates.has(ds);
                const isStart = selectedStartDate === ds;
                const isToday = ds === todayStr;
                return (
                  <div
                    key={`${ds}-${idx}`}
                    onClick={() => handleDateClick(ds)}
                    style={{
                      padding: '8px 0',
                      textAlign: 'center',
                      fontSize: '13px',
                      borderBottom: '1px solid #f3f4f6',
                      borderRight: idx % 7 !== 6 ? '1px solid #f3f4f6' : 'none',
                      color: !inMonth
                        ? '#d1d5db'
                        : !selectable
                        ? '#9ca3af'
                        : '#111827',
                      background: isStart
                        ? '#1d4ed8'
                        : selected
                        ? '#3b82f6'
                        : isToday && selectable
                        ? '#eff6ff'
                        : 'white',
                      fontWeight: selected ? 700 : isToday ? 600 : 400,
                      cursor: selectable ? 'pointer' : 'not-allowed',
                      userSelect: 'none',
                      ...(selected ? { color: 'white' } : {}),
                    }}
                  >
                    {day.getDate()}
                  </div>
                );
              })}
            </div>
          </div>
          <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
            진한 파랑: 시작일 / 파랑: 자동 선택된 범위(시작일 ~ 월 말일) / 회색: 과거·다른 월(선택 불가) / 연한 파랑: 오늘
          </p>
        </div>

        {/* 2) 시간 입력 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              출근 시간
            </label>
            <TimeSelect value={startTime} onChange={setStartTime} />
          </div>
          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              점심 시작
            </label>
            <TimeSelect value={lunchStart} onChange={setLunchStart} />
          </div>
          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              점심 종료
            </label>
            <TimeSelect value={lunchEnd} onChange={setLunchEnd} />
          </div>
          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              퇴근 시간
            </label>
            <TimeSelect value={endTime} onChange={setEndTime} />
          </div>

          {/* 템플릿 선택 (시간 prefill 용) */}
          {templates.length > 0 && (
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                템플릿 설정 (선택)
              </label>
              <select
                value={selectedTemplateId || ''}
                onChange={(e) => handleTemplateSelect(e.target.value || null)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                <option value="">직접 입력</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                템플릿 선택 시 base 시간이 4개 입력란에 채워지며, 템플릿에 요일별 차등(예: 월/금)이 있으면
                선택한 날짜들에 자동으로 요일별 시간이 반영됩니다.
              </p>
              {(() => {
                if (!selectedTemplateId) return null;
                const tpl = templates.find((t) => t.id === selectedTemplateId);
                if (!tpl) return null;
                const tplResult = tpl.generate(year, monthNum); // V66c-2: 1-indexed
                const dayMap = new Map<string, any>();
                tplResult.days.forEach((d: any) => dayMap.set(d.date, d));
                let overrideCount = 0; // base 와 다른 근무일
                let offCount = 0;      // 휴무 (놀금/주말)
                const baseS = tplResult.base;
                Array.from(selectedDates).forEach((d) => {
                  const e = dayMap.get(d);
                  if (!e) return;
                  if (e.isOff) offCount++;
                  else if (e.schedule) {
                    const isBase =
                      e.schedule.startTime === baseS.startTime &&
                      e.schedule.endTime === baseS.endTime &&
                      e.schedule.lunchStart === baseS.lunchStart &&
                      e.schedule.lunchEnd === baseS.lunchEnd;
                    if (!isBase) overrideCount++;
                  }
                });
                if (overrideCount === 0 && offCount === 0) return null;
                const parts: string[] = [];
                if (overrideCount > 0) parts.push(`${overrideCount}일 요일별 차등`);
                if (offCount > 0) parts.push(`${offCount}일 휴무 (놀금/주말)`);
                return (
                  <p style={{ fontSize: '11px', color: '#10b981', marginTop: '4px' }}>
                    ✓ 선택 날짜에 템플릿 자동 적용: <strong>{parts.join(', ')}</strong>
                  </p>
                );
              })()}
            </div>
          )}

          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              변경 사유 (선택)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="예: 외부 프로젝트 일정 반영"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
              }}
            />
          </div>
        </div>

        {/* 3) 버튼 */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: '12px',
              background: '#f3f4f6',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || selectedDates.size === 0}
            style={{
              flex: 1,
              padding: '12px',
              background:
                isSubmitting || selectedDates.size === 0 ? '#9ca3af' : '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor:
                isSubmitting || selectedDates.size === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting
              ? '신청 중...'
              : `일괄수정 신청 (${selectedDates.size}일)`}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
